"use client"

import { useState, useEffect } from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog"
import { Badge } from "../ui/badge"
import { Textarea } from "../ui/textarea"
import { Button } from "../ui/button"
import { CheckCircle, XCircle, Clock, X, Check } from 'lucide-react' // Added icons for approval status
import axios from "axios"
import { apiClient } from "../lib/apiClient"
import { format } from "date-fns"

interface LeaveReviewModalHRProps {
  isOpen: boolean
  onClose: () => void
  leaveRequest: any // Consider defining a more specific type for leaveRequest
  isViewOnly?: boolean // New prop to control button visibility
  onApprove: (comment: string) => void;
  onReject: (comment: string) => void;
}


export function LeaveReviewModalHR({ isOpen, onClose, leaveRequest, isViewOnly = false, onApprove, onReject }: LeaveReviewModalHRProps) {
  const formatDateTime = (value?: string) => {
    if (!value) return "";
    try {
      const d = new Date(value)
      return `${d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`.replace(/\s/g, "-")
    } catch {
      return value
    }
  }

  // Derive manager details from leaveRequest if present
  const managerName = (leaveRequest?.managerName || "--")
  const [resolvedManagerName, setResolvedManagerName] = useState<string>("")

  useEffect(() => {
    const resolveName = async () => {
      try {
        if (!managerName && leaveRequest?.modUser && leaveRequest.modUser !== 0) {
          const res = await apiClient.get("/user/getallusers")
          const users: any[] = res?.data?.users || res?.data || []
          const match = users.find((u: any) => (u.userID ?? u.UserID) === leaveRequest.modUser)
          if (match) {
            setResolvedManagerName(match.userName ?? match.UserName ?? "")
          }
        } else {
          setResolvedManagerName("")
        }
      } catch {
        setResolvedManagerName("")
      }
    }
    if (isOpen) resolveName()
  }, [isOpen, managerName, leaveRequest?.modUser])

  const managerApprovedBy = (managerName && typeof managerName === 'string' && managerName !== '0')
    ? managerName
    : (resolvedManagerName || (leaveRequest?.modUser && leaveRequest.modUser !== 0 ? `Manager (ID: ${leaveRequest.modUser})` : "-"))

  const managerApprovedOn = leaveRequest?.managerResponseOn
    ? formatDateTime(leaveRequest.managerResponseOn)
    : ("--")

  const rawManagerResponse: string = String(leaveRequest?.managerResponse || "").toLowerCase()
  const managerStatus = rawManagerResponse.includes("approved")
    ? "Approved"
    : rawManagerResponse.includes("rejected")
      ? "Rejected"
      : (
        // Fallback inference if explicit managerResponse isn't present
        (leaveRequest?.status === "approved" || leaveRequest?.status === "pending_hr_approval" || leaveRequest?.status === "rejected_by_hr")
          ? "Approved"
          : leaveRequest?.status === "rejected"
            ? "Rejected"
            : "Pending"
      )

  const managerComment = (leaveRequest?.comments || "").trim() || (managerStatus === "Approved" ? "Approved" : managerStatus === "Rejected" ? "Rejected" : "")

  const mockApprovalStatus = {
    manager: {
      status: managerStatus,
      approvedBy: managerApprovedBy,
      approvedOn: managerApprovedOn,
      comment: managerComment,
    },
    hr: {
      status: leaveRequest?.status === "approved"
        ? "Approved"
        : leaveRequest?.status === "rejected_by_hr"
          ? "Rejected"
          : "Pending",
      hrResponse : leaveRequest?.hrResponse || "--",
      approvedBy: leaveRequest?.hrApprovedBy || "--",
      approvedOn: leaveRequest?.hrResponseOn || "--",
      comment: leaveRequest?.hrComment || "--",
    },
  }

  const getApprovalBadge = (status: string) => {
    switch (status) {
      case "Approved":
        return <Badge className="bg-green-100 text-green-700 flex items-center gap-1"><CheckCircle className="h-3 w-3" /> Approved</Badge>
      case "Pending":
        return <Badge className="bg-orange-100 text-orange-700 flex items-center gap-1"><Clock className="h-3 w-3" /> Pending</Badge>
      case "Rejected":
        return <Badge className="bg-red-100 text-red-700 flex items-center gap-1"><XCircle className="h-3 w-3" /> Rejected</Badge>
      default:
        return <Badge variant="outline">{status}</Badge>
    }
  }
  const [comment, setComment] = useState<string>("")
  const [submitting, setSubmitting] = useState<boolean>(false)

  useEffect(() => {
    if (isOpen) {
      setComment("")
    }
  }, [isOpen, leaveRequest?.requestID, leaveRequest?.id])

  const callHrAction = async (responseAction: "approved" | "rejected") => {
    if (!leaveRequest?.id && !leaveRequest?.requestID) return
    setSubmitting(true)
    try {
      await axios.post(
        process.env.REACT_APP_API_URL + "/api/Leave/ProcessHRAction",
        { requestID: leaveRequest.id ?? leaveRequest.requestID, response: responseAction === "approved" ? "Approved" : "Rejected", comment: comment || "", modUser: 0 },
        { withCredentials: true }
      )
      if (responseAction === "approved") {
        onApprove(comment)
      } else {
        onReject(comment)
      }
    } catch (e) {
      // Error handling is done in parent component
    } finally {
      setSubmitting(false)
      setComment("")
      onClose()
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-full max-w-full sm:max-w-lg bg-white border border-gray-200 rounded-lg shadow-lg mx-2 sm:mx-auto max-h-[90vh] flex flex-col p-0">
        {/* Fixed Header */}
        <DialogHeader className="border-b border-gray-200 pb-4 px-4 sm:px-6 pt-6 flex flex-row items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <DialogTitle className="text-lg sm:text-xl font-bold text-gray-900">
              {leaveRequest?.requestBy}
            </DialogTitle>
            <Badge variant="outline" className="text-orange-600 border-orange-200 bg-orange-50 text-xs sm:text-sm font-medium">
              {leaveRequest?.leaveType}
            </Badge>
          </div>
        </DialogHeader>

        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto custom-scrollbar px-4 sm:px-6 py-4">
          <div className="space-y-4">
            <div className="text-sm text-gray-600">
              <strong className="text-gray-900">Applied On:</strong> {leaveRequest?.appliedOn}
            </div>

            {/* Manager Approval Details */}
            <div className="border border-gray-200 rounded-lg p-3 sm:p-4 bg-gray-50 shadow-sm">
              <div className="space-y-2">
                <div className="font-medium text-gray-900 text-sm sm:text-base flex items-center gap-2">
                  Manager Approval {getApprovalBadge(mockApprovalStatus.manager.status)}
                </div>
                {mockApprovalStatus.manager.status !== "Pending" && (
                  <>
                    <div className="text-xs sm:text-sm text-gray-600">
                      <strong className="text-gray-900">Reviewed By:</strong> {mockApprovalStatus.manager.approvedBy}
                    </div>
                    <div className="text-xs sm:text-sm text-gray-600">
                      <strong className="text-gray-900">Reviewed On:</strong> {mockApprovalStatus.manager.approvedOn}
                    </div>
                    <div className="text-xs sm:text-sm">
                      <strong className="text-gray-900">Comment:</strong>
                      <span className="text-gray-700 ml-1 break-all whitespace-pre-wrap">{mockApprovalStatus.manager.comment}</span>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* HR Approval Details */}
            <div className="border border-gray-200 rounded-lg p-3 sm:p-4 bg-gray-50 shadow-sm">
              <div className="space-y-2">
                <div className="font-medium text-gray-900 text-sm sm:text-base flex items-center gap-2">
                  HR Approval {getApprovalBadge(mockApprovalStatus.hr.status)}
                </div>
                {mockApprovalStatus.hr.status !== "Pending" && (
                  <>
                    <div className="text-xs sm:text-sm text-gray-600">
                      <strong className="text-gray-900">Reviewed By:</strong> {mockApprovalStatus.hr.approvedBy}
                    </div>
                    <div className="text-xs sm:text-sm text-gray-600">
                      <strong className="text-gray-900">Reviewed On:</strong> {mockApprovalStatus.hr.approvedOn}
                    </div>
                    <div className="text-xs sm:text-sm">
                      <strong className="text-gray-900">Comment:</strong>
                      <span className="text-gray-700 ml-1 break-all whitespace-pre-wrap">{mockApprovalStatus.hr.comment}</span>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Original Duration and Reason */}
            <div className="border border-gray-200 rounded-lg p-3 sm:p-4 bg-gray-50 shadow-sm">
              <div className="space-y-2">
                <div className="font-medium text-gray-900 text-sm sm:text-base">
                  Duration ({leaveRequest?.totalDays || "1"} Days)
                </div>
                <div className="text-xs sm:text-sm text-gray-600">
                  {leaveRequest?.fromDate || "-"} - {leaveRequest?.toDate || "-"}
                </div>
                <div className="text-xs sm:text-sm">
                  <strong className="text-gray-900">Reason:</strong>
                  <span className="text-gray-700 ml-1">{leaveRequest?.reason || "-"}</span>
                </div>
              </div>
            </div>

            {/* Leave Day Details */}
            {(() => {
              // Try multiple possible property names
              const dayDetails = leaveRequest?.leaveDayDetails || leaveRequest?.leaveDayDetail || leaveRequest?.leaveDays || [];
              const isValidArray = Array.isArray(dayDetails) && dayDetails.length > 0;
              
              // Debug: Log to help identify the issue
              if (isOpen) {
                ////console.log("Full leaveRequest in HR Modal:", leaveRequest);
                ////console.log("Leave Day Details (leaveDayDetails):", leaveRequest?.leaveDayDetails);
                ////console.log("Leave Day Details (leaveDayDetail):", leaveRequest?.leaveDayDetail);
                ////console.log("Leave Day Details (leaveDays):", leaveRequest?.leaveDays);
                ////console.log("Final dayDetails:", dayDetails);
                ////console.log("Is Valid Array:", isValidArray);
                ////console.log("Type of dayDetails:", typeof dayDetails);
                if (dayDetails) {
                  ////console.log("dayDetails.length:", dayDetails.length);
                }
              }
              
              return isValidArray ? (
                <div className="border border-gray-200 rounded-lg p-3 sm:p-4 bg-gray-50 shadow-sm">
                  <div className="font-medium text-gray-900 text-sm sm:text-base mb-3">
                    Leave Day Details
                  </div>
                  <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-md bg-white custom-scrollbar">
                    <div className="divide-y divide-gray-200">
                      {dayDetails.map((dayDetail: any, index: number) => {
                        // Format dayType for display
                        const formatDayType = (dayType: string) => {
                          if (!dayType) return "Full Day";
                          const lower = dayType.toLowerCase();
                          if (lower === "first-half" || lower === "firsthalf") return "First-Half";
                          if (lower === "second-half" || lower === "secondhalf") return "Second-Half";
                          return "Full Day";
                        };

                        // Format date
                        const formatDateDisplay = (dateString: string | null | undefined) => {
                          if (!dateString) return "--";
                          try {
                            const date = new Date(dateString);
                            if (isNaN(date.getTime())) return String(dateString);
                            return format(date, "dd-MM-yyyy");
                          } catch {
                            return "--";
                          }
                        };
                        
                        return (
                          <div key={index} className="p-3 hover:bg-gray-50">
                            <div className="flex items-center justify-between">
                              <div className="text-xs sm:text-sm font-medium text-gray-900">
                                {formatDateDisplay(dayDetail.leaveDate)}
                              </div>
                              <div className="text-xs sm:text-sm text-gray-600">
                                {formatDayType(dayDetail.dayType)}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ) : null;
            })()}

            {!isViewOnly && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-xs sm:text-sm font-medium text-gray-700">Comment (optional)</label>
                  <span className="text-xs text-gray-500">
                    {comment.length}/1000
                  </span>
                </div>
                <Textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Add a comment for your decision"
                  disabled={
                    (leaveRequest?.numericStatus === 3) ||
                    (leaveRequest?.numericStatus === 8) ||
                    (leaveRequest?.numericStatus === 9) ||
                    submitting
                  }
                  className="min-h-[100px] bg-white border-gray-200 text-gray-900 text-sm sm:text-base disabled:opacity-70"
                  maxLength={1000}
                />
              </div>
            )}
          </div>
        </div>

        {/* Fixed Buttons */}
        <div className="border-t border-gray-200 px-4 sm:px-6 py-4 flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 flex-shrink-0 bg-white">
          {isViewOnly ? (
            <Button onClick={onClose} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 sm:px-6 sm:py-2.5 text-sm sm:text-base font-medium rounded-md shadow-sm w-full sm:w-auto">
              Close
            </Button>
          ) : (
            <>
              <Button 
                variant="destructive" 
                onClick={() => callHrAction("rejected")} 
                disabled={
                  !(mockApprovalStatus.manager.status === "Approved" && mockApprovalStatus.hr.status === "Pending") || submitting
                }
                className="flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 text-sm sm:text-base font-medium rounded-md shadow-sm w-full sm:w-auto"
              >
                <X className="h-3 w-3" />
                Reject
              </Button>
              <Button 
                onClick={() => callHrAction("approved")}
                disabled={
                  !(mockApprovalStatus.manager.status === "Approved" && mockApprovalStatus.hr.status === "Pending") || submitting
                } 
                className="flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 text-sm sm:text-base font-medium rounded-md shadow-sm w-full sm:w-auto"
              >
                <Check className="h-3 w-3" />
                Approve
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
